import { RoomBox, useRoomBoxes } from '../bughunt/RoomBoxes'
import { useAgentLog } from '../bughunt/useAgentLog'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight, FileText, FlaskConical, PanelBottomClose, PanelBottomOpen, PanelRightClose, PanelRightOpen, Plus, Quote,
  Search, Undo2, UserRound, Volume2, VolumeX, XCircle,
} from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatDuration, formatTime } from '../lib/format'
import { realtime, type ConnectionState } from '../lib/realtime'
import { usePrefersReducedMotion } from '../lib/theme'
import { atLeast, getSession } from '../lib/auth'
import { BugHuntStore, type BugEntity, type DeveloperEntity } from '../bughunt/store'
import { Scene } from '../bughunt/Scene'
import { agentActivity, useSceneLabels } from '../bughunt/useSceneLabels'
import { BugCreature, Portrait, lookFor } from '../bughunt/Figures'
import { Badge, EmptyState, ProgressBar } from '../components/primitives'
import { TeamPicker } from '../components/TeamPicker'
import { SceneryPicker } from '../components/SceneryPicker'
import { LinkedDocs } from '../components/LinkedDocs'
import { useBugControls } from '../bughunt/useBugControls'
import { combineControls, usePersonControls } from '../bughunt/usePersonControls'
import { useRoomChat } from '../bughunt/useRoomChat'
import { RoomChat } from '../components/RoomChat'
import { BugComments } from '../bughunt/BugComments'
import { AgentCard, DeveloperFocus, SessionTabs } from '../bughunt/SessionPanels'
import { fromBugHunt } from '../bughunt/panelSession'
import { formatScope, useTeamFilter } from '../lib/teamFilter'
import { usePersistedFlag } from '../lib/usePersistedFlag'
import { OfficeArrows, OfficePicker, useWalkTo } from '../bughunt/OfficeNav'
import { routeFor } from '../bughunt/office'
import type { BugState, Priority } from '../lib/types'

/**
 * The Bug Hunt.
 *
 * Real telemetry drawn as a room. The store holds normalised game state, the scene renders it as
 * SVG, and the panels around it show the same facts as numbers and rows — several views of one
 * fact, never several facts.
 *
 * The layout is a game screen: counters down the left of the room, the bug list down the right,
 * and under the room whoever is in focus — their session, its log, and what their agent is doing
 * now. Focus is whoever was clicked, or else the first person with an agent running, so the panels
 * are never empty while something is happening.
 *
 * The whole page is dark whatever the theme. The room is a night scene, and a light frame around a
 * dark illustration reads as a hole in the page rather than as a view into a room.
 *
 * It is one room of the office (docs/refactor/29): the same dropdown and side arrows as the floor's
 * areas lead to the main hall, the kitchen and the war room. Every agent at work is here too, beside
 * its person, whatever it works on — the Scene draws each person's sessions as their agents.
 */

const FILTERS: Array<{ value: string; key: string }> = [
  { value: 'all', key: 'common.all' },
  { value: 'Waiting', key: 'bugHunt.waiting' },
  { value: 'Assigned', key: 'bugHunt.assigned' },
  { value: 'InProgress', key: 'bugHunt.inProgress' },
  { value: 'Testing', key: 'bugHunt.testing' },
  { value: 'InTesting', key: 'bugState.InTesting' },
  { value: 'Blocked', key: 'bugHunt.blocked' },
  { value: 'ReadyForReview', key: 'bugHunt.readyForReview' },
  { value: 'Completed', key: 'bugHunt.completedToday' },
]

export default function BugHunt() {
  // The three boxes under the room, each foldable, and all three at once (owner, 08/10/2026).
  const boxes = useRoomBoxes()
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const reducedMotion = usePrefersReducedMotion()
  const session = getSession()
  const canReview = atLeast(session?.role, 'Developer')

  const storeRef = useRef<BugHuntStore>()
  if (!storeRef.current) storeRef.current = new BugHuntStore()
  const store = storeRef.current

  const [registering, setRegistering] = useState(false)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [sound, setSound] = useState(() => {
    try {
      return localStorage.getItem('devpulse:bughunt-sound') === 'on'
    } catch {
      return false
    }
  })

  const gameState = useSyncExternalStore(store.subscribe, store.snapshot)

  // For more room: the panels under the room and the bug list each fold away, and stay folded.
  const [showPanels, togglePanels] = usePersistedFlag('devpulse:bughunt-panels', true)
  const [showList, toggleList] = usePersistedFlag('devpulse:bughunt-list', true)

  // A manager looks at everybody, a team, a squad or one person. The waiting jar is always shown.
  const team = useTeamFilter()
  const { teamId, developerId } = team
  const scopeKey = formatScope(team.scope) || 'all'
  const scoped = scopeKey !== 'all'

  const { data, isLoading } = useQuery({
    queryKey: ['bug-hunt', 'state', scopeKey],
    queryFn: () => api.bugHunt.state({ teamId, developerId }),
    enabled: team.ready,
  })

  // Hydration is authoritative and happens on load, on reconnect and whenever the query refetches.
  // §36: a manager opening the page 40 minutes in sees 68% immediately, not 40 minutes of replay.
  useEffect(() => {
    if (data) store.hydrate(data)
  }, [data, store])

  useEffect(
    () =>
      realtime.onBugEvents((events) => {
        store.enqueue(scoped ? events.filter((event) => store.concerns(event)) : events)

        // Any transition that moves a bug between states changes the counters, and the incremental
        // events do not carry counts. Written as an exclusion rather than a list of kinds, because
        // a list of the kinds that count is exactly the kind of list that rots.
        const movesBetweenStates = events.some(
          (event) => event.kind !== 'BugProgressChanged' && event.kind !== 'DeveloperWorkStateChanged',
        )

        if (movesBetweenStates) {
          void queryClient.invalidateQueries({ queryKey: ['bug-hunt'] })
        }
      }),
    [store, queryClient, scoped],
  )

  const labels = useSceneLabels()

  /*
    One selection, shared by the scene and the panels.

    Clicking a bug on the floor selects its row; clicking a row highlights the bug and its
    developer. Two views of one fact that can disagree is a defect this screen has already had
    once — the counters and the list — so there is a single piece of state and both read it.
  */
  const selectBug = useCallback(
    (taskId: string | null) => {
      const bug = taskId ? store.snapshot().bugs.get(taskId) : null
      store.select(bug?.developerId ?? null, taskId)
    },
    [store],
  )

  const selectDeveloper = useCallback(
    (developerId: string | null) => store.select(developerId, null),
    [store],
  )

  // A link from elsewhere — a document, a report — can open the page on one bug.
  const [params] = useSearchParams()
  const linkedBug = params.get('bug')
  useEffect(() => {
    if (linkedBug && data && store.snapshot().bugs.has(linkedBug)) selectBug(linkedBug)
  }, [linkedBug, data, store, selectBug])

  const openContainment = useCallback(() => setFilter('Waiting'), [])

  // Right-click a mosquito for its menu; drag it to the jar or to a person. For those who may move bugs.
  const bugControls = useBugControls({ store, enabled: canReview, onOpenBug: selectBug })

  // Speak in the room from your own character; right-click anybody for their menu.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const chat = useRoomChat()

  // The office's other rooms; walking your character to an area takes you there with it.
  const navigate = useNavigate()
  const goPlace = useCallback((place: Parameters<typeof routeFor>[0]) => navigate(routeFor(place)), [navigate])
  const moveMe = useWalkTo(goPlace)

  const personControls = usePersonControls({
    meDeveloperId: me?.developerId,
    nameOf: (id) => store.snapshot().developers.get(id)?.name ?? '',
    onSay: chat.say,
    moveTo: { go: moveMe },
  })
  const handlers = combineControls(bugControls.handlers, personControls.handlers)

  const transition = useMutation({
    mutationFn: ({ taskId, action, reason }: { taskId: string; action: string; reason?: string }) =>
      api.bugHunt.transition(taskId, action, undefined, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bug-hunt'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const bugs = useMemo(() => {
    const all = [...gameState.bugs.values()]
    const term = search.trim().toLowerCase()

    return all
      .filter((bug) => filter === 'all' || bug.state === (filter as BugState))
      .filter((bug) => {
        if (!term) return true
        const developer = bug.developerId ? gameState.developers.get(bug.developerId) : undefined
        return (
          bug.externalId.toLowerCase().includes(term) ||
          bug.title.toLowerCase().includes(term) ||
          Boolean(developer?.name.toLowerCase().includes(term))
        )
      })
      .sort((a, b) => {
        const rank: Record<string, number> = { Blocked: 0, Testing: 1, InProgress: 2, ReadyForReview: 3 }
        return (rank[a.state] ?? 9) - (rank[b.state] ?? 9)
      })
  }, [gameState.bugs, gameState.developers, filter, search])

  const selectedBug = gameState.selectedBugId ? gameState.bugs.get(gameState.selectedBugId) : undefined

  // Whoever the panels are about: the person clicked, the person on the bug clicked, or else the
  // first person with an agent running.
  const focus = useMemo((): DeveloperEntity | undefined => {
    if (gameState.selectedDeveloperId) return gameState.developers.get(gameState.selectedDeveloperId)

    const everyone = [...gameState.developers.values()]
    return (
      everyone.find((d) => d.sessions.length > 0 && d.state !== 'Idle') ??
      everyone.find((d) => d.sessions.length > 0) ??
      everyone[0]
    )
  }, [gameState.selectedDeveloperId, gameState.developers])

  // Each agent has its own log, as in the follow-up room (bughunt/useAgentLog.tsx): a click on an agent picks it.
  const agentLog = useAgentLog({ personId: focus?.id, sessions: focus?.sessions ?? [], agentLooks: focus?.agentLooks, enabled: showPanels })

  const focusSession =
    // A bug clicked shows its session; otherwise the agent picked, or the first.
    focus?.sessions.find((s) => selectedBug && s.workSessionId === selectedBug.workSessionId) ?? agentLog.picked ?? focus?.sessions[0]

  const panelSession = useMemo(() => (focusSession ? fromBugHunt(focusSession) : undefined), [focusSession])

  const focusBug = focusSession
    ? [...gameState.bugs.values()].find((bug) => bug.workSessionId === focusSession.workSessionId)
    : undefined

  // One fetch for all three panels. Polled, because files, tokens and cost change with every
  // event and the bug stream does not carry them.
  const { data: detail } = useQuery({
    queryKey: ['work', focusSession?.workSessionId],
    queryFn: () => api.sessions.detail(focusSession!.workSessionId),
    enabled: Boolean(focusSession?.workSessionId),
    refetchInterval: 10_000,
  })

  const counts = data?.counts ?? gameState.counts

  if (data && !data.enabled) {
    return (
      <div style={{ padding: 'var(--s6)' }}>
        <EmptyState title={t('bugHunt.disabled')} body={t('bugHunt.disabledHint')} />
      </div>
    )
  }

  return (
    <div className={`bughunt theme-night${showPanels ? '' : ' bughunt--no-bottom'}${showList ? '' : ' bughunt--no-list'}`}
      {...handlers}
      onClickCapture={(event) => {
        agentLog.pickFromClick(event)
        handlers.onClickCapture(event)
      }}
    >
      {bugControls.overlay}
      {personControls.overlay}
      <section className="bughunt__stage" aria-label={t('bugHunt.title')}>
        <Scene
          state={gameState}
          labels={labels}
          reducedMotion={reducedMotion}
          onSelectBug={selectBug}
          onSelectDeveloper={selectDeveloper}
          onOpenContainment={openContainment}
          speech={chat.speech}
        />

        <RoomChat chat={chat} meDeveloperId={me?.developerId} />

        {/* Clear of the counters down the left edge. */}
        <OfficeArrows current="bugs" onGo={goPlace} inset={124} />

        <div className="bughunt__kpis">
          <Kpi label={t('bugHunt.total')} value={counts.total} />
          <Kpi label={t('bugHunt.inProgress')} value={counts.inProgress} tone="accent" />
          <Kpi label={t('bugHunt.waiting')} value={counts.waiting + counts.assigned} tone="warn" />
          <Kpi label={t('bugHunt.readyForReview')} value={counts.readyForReview} tone="review" />
          <Kpi label={t('bugHunt.completedToday')} value={counts.completedToday} tone="ok" />
          <Kpi label={t('bugHunt.blocked')} value={counts.blocked} tone="danger" />
        </div>

        <div className="bughunt__stage-controls">
          <OfficePicker current="bugs" onGo={goPlace} />
          <TeamPicker filter={team} />
          <SceneryPicker />
          <LivePill />
          <button
            type="button"
            className="bughunt__icon-button"
            onClick={() => {
              const next = !sound
              setSound(next)
              try {
                localStorage.setItem('devpulse:bughunt-sound', next ? 'on' : 'off')
              } catch {
                // Storage blocked: the toggle still works for this visit.
              }
            }}
            title={t('bugHunt.sound')}
            aria-label={t('bugHunt.sound')}
          >
            {sound ? <Volume2 size={14} /> : <VolumeX size={14} />}
          </button>
          <button
            type="button"
            className="bughunt__icon-button bughunt__fold"
            onClick={togglePanels}
            aria-pressed={!showPanels}
            title={showPanels ? t('bugHunt.hidePanels') : t('bugHunt.showPanels')}
            aria-label={showPanels ? t('bugHunt.hidePanels') : t('bugHunt.showPanels')}
          >
            {showPanels ? <PanelBottomClose size={14} /> : <PanelBottomOpen size={14} />}
          </button>
          <button
            type="button"
            className="bughunt__icon-button bughunt__fold"
            onClick={toggleList}
            aria-pressed={!showList}
            title={showList ? t('bugHunt.hideList') : t('bugHunt.showList')}
            aria-label={showList ? t('bugHunt.hideList') : t('bugHunt.showList')}
          >
            {showList ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
          </button>
        </div>

        {reducedMotion && <div className="bughunt__motion-note">{t('bugHunt.reducedMotion')}</div>}
        {isLoading && <div className="bughunt__loading">{t('common.loading')}</div>}

        {/*
          The accessible mirror of the scene. SVG elements are focusable and labelled, so the floor
          is navigable in its own right, but a list is a faster read for a screen reader than
          tabbing through a room.
        */}
        <div className="sr-only" aria-live="polite">
          <h2>{t('bugHunt.title')}</h2>
          <ul>
            {[...gameState.developers.values()].map((developer) => (
              <li key={developer.id}>
                {developer.name}: {developer.sessions.length > 0 ? agentActivity(developer.state, t) : t('activity.noAgent')}
                {developer.sessions[0] &&
                  ` — ${developer.sessions[0].taskExternalId ?? ''} ${Math.round(developer.sessions[0].progressPercent)}%`}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={`bughunt__bottom${boxes.allClosed ? ' room-boxes--folded' : ''}`}>
        <RoomBox id="person" title={t('room.boxes.person')} boxes={boxes}>
        <div className="bh-panel bh-focus">
          {selectedBug ? (
            <BugDetail
              bug={selectedBug}
              developer={selectedBug.developerId ? gameState.developers.get(selectedBug.developerId) : undefined}
              canReview={canReview}
              onAction={(action, reason) => transition.mutate({ taskId: selectedBug.taskId, action, reason })}
              onClose={() => store.select(null, null)}
            />
          ) : focus ? (
            <DeveloperFocus
              person={focus}
              working={Boolean(focusSession) && focus.state !== 'Idle' && focus.state !== 'Completed'}
              session={panelSession}
              detail={detail}
              lead={<BugGlyph priority={focusBug?.priority ?? 'Medium'} size={22} />}
              tag={focusBug && <Badge tone={priorityTone(focusBug.priority)}>{t(`priority.${focusBug.priority}`)}</Badge>}
            />
          ) : (
            <EmptyState title={t('bugHunt.selectDeveloper')} />
          )}
        </div>
        </RoomBox>

        <RoomBox id="log" title={t('room.boxes.log')} boxes={boxes}>
        <div className="bh-panel">
          {agentLog.switcher}
          <SessionTabs
            key={`${panelSession?.workSessionId ?? 'none'}${agentLog.all ? ':all' : ''}`}
            session={panelSession}
            detail={agentLog.merge(detail)}
            // Every agent together reads their sessions' merged history; the room's live log only knows one stream.
            log={focus && !agentLog.all ? gameState.log.filter((entry) => entry.developerId === focus.id) : []}
          />
        </div>

        </RoomBox>

        <RoomBox id="agent" title={t('room.boxes.agent')} boxes={boxes}>
        <AgentCard
          session={panelSession}
          detail={detail}
          decoration={
            <svg className="bh-agentcard__bug" viewBox="-30 -40 70 70" aria-hidden="true">
              <g className="bh-palette">
                <BugCreature priority={focusBug?.priority ?? 'Medium'} seed={4} weakened={false} still={false} />
              </g>
            </svg>
          }
        />
        </RoomBox>
      </section>

      <aside className="bh-panel bughunt__list-panel" aria-label={t('bugHunt.bugs')}>
        <header className="bughunt__list-header">
          <h2 className="bughunt__list-title">{t('bugHunt.bugs')}</h2>
          <div className="spacer" />
          <select
            className="select bughunt__filter"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            aria-label={t('bugHunt.filter')}
          >
            {FILTERS.map((option) => (
              <option key={option.value} value={option.value}>{t(option.key)}</option>
            ))}
          </select>
          {canReview && (
            <button
              type="button"
              className="bughunt__icon-button"
              onClick={() => setRegistering((open) => !open)}
              aria-expanded={registering}
              title={t('bugHunt.registerBug')}
              aria-label={t('bugHunt.registerBug')}
            >
              <Plus size={15} />
            </button>
          )}
        </header>

        {registering && <RegisterBugForm onClose={() => setRegistering(false)} />}

        <label className="bughunt__search">
          <Search size={14} className="muted" />
          <input
            className="input"
            placeholder={t('bugHunt.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>

        <div className="bughunt__list">
          {bugs.length === 0 ? (
            <EmptyState title={t('bugHunt.noBugs')} />
          ) : (
            bugs.map((bug) => (
              <BugRow
                key={bug.taskId}
                bug={bug}
                developer={bug.developerId ? gameState.developers.get(bug.developerId) : undefined}
                active={gameState.selectedBugId === bug.taskId}
                onClick={() => selectBug(gameState.selectedBugId === bug.taskId ? null : bug.taskId)}
              />
            ))
          )}
        </div>

        {/*
          Where the mock-up put a quotation attributed to a developer. Nobody said it, and a screen
          that invents what people say is not a screen anyone should trust with what agents did.
          The rule that governs what happens when an agent finishes is worth the space instead.
        */}
        <figure className="bh-quote">
          <Quote size={16} className="bh-quote__mark" />
          <blockquote>{t(`bugHunt.policyNote.${gameState.completionPolicy}`)}</blockquote>
          <figcaption>{t('bugHunt.completionPolicy')}</figcaption>
        </figure>
      </aside>
    </div>
  )
}

/* ------------------------------------------------------------------- stage */

/** One counter down the side of the room. */
function Kpi({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="bh-kpi" data-tone={tone}>
      <span className="bh-kpi__value">{value}</span>
      <span className="bh-kpi__label">{label}</span>
    </div>
  )
}

/**
 * Whether the room is live, and the time.
 *
 * Only says "live" when the socket actually is. A clock ticking beside a dead connection would
 * be the most convincing way to show stale data as current.
 */
function LivePill() {
  const { t, locale } = useI18n()
  const [state, setState] = useState<ConnectionState>(realtime.connectionState)
  const [now, setNow] = useState(() => new Date())

  useEffect(() => realtime.onStateChange(setState), [])

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(timer)
  }, [])

  const live = state === 'connected'

  return (
    <span className={`bughunt__live${live ? ' bughunt__live--on' : ''}`}>
      <span className="bughunt__live-dot" />
      {live ? t('bugHunt.live') : t('bugHunt.reconnecting')}
      <span className="mono">{formatTime(now, locale)}</span>
    </span>
  )
}

/* ------------------------------------------------------------------- focus */

/** A bug in focus, with the actions a person can take on it. */
function BugDetail({
  bug,
  developer,
  onAction,
  onClose,
  canReview,
}: {
  bug: BugEntity
  developer: DeveloperEntity | undefined
  onAction: (action: string, reason?: string) => void
  onClose: () => void
  canReview: boolean
}) {
  const { t, locale } = useI18n()
  const [returning, setReturning] = useState(false)
  const [reason, setReason] = useState('')

  return (
    <div className="stack stack--tight">
      <div className="row row--tight">
        <BugGlyph priority={bug.priority} size={26} />
        <span className="mono bold">{bug.externalId}</span>
        <Badge tone={priorityTone(bug.priority)}>{t(`priority.${bug.priority}`)}</Badge>
        <div className="spacer" />
        <button type="button" className="bughunt__icon-button" onClick={onClose} aria-label={t('common.close')}>
          <XCircle size={14} />
        </button>
      </div>

      <span className="small">{bug.title}</span>

      <SpecLink bug={bug} canEdit={canReview} />

      <LinkedDocs taskId={bug.taskId} canEdit={canReview} />

      <div className="row row--tight">
        <Badge tone={stateTone(bug.state)}>{t(`bugState.${bug.state}`)}</Badge>
        {bug.reopenCount > 0 && <Badge tone="warn">{t('bugState.Reopened')} ×{bug.reopenCount}</Badge>}
        {bug.isAgentDiscovered && <Badge tone="warn">{t('bugHunt.agentDiscovered')}</Badge>}
        {developer && <span className="tiny muted truncate">{developer.name}</span>}
        {bug.state === 'InTesting' && bug.testerName && (
          <span className="tiny" style={{ color: 'var(--accent-text)' }}>{t('bugHunt.testedBy', { name: bug.testerName })}</span>
        )}
      </div>

      {bug.progressPercent > 0 && <ProgressBar value={bug.progressPercent} showValue size="sm" />}

      {bug.blockerReason && <span className="tiny" style={{ color: 'var(--danger-text)' }}>{bug.blockerReason}</span>}

      <div className="row row--tight tiny muted">
        {bug.testsTotal > 0 && <span>{t('live.tests')}: {bug.testsPassed}/{bug.testsTotal}</span>}
        {bug.elapsedSeconds != null && <span>{t('bugHunt.elapsed')}: {formatDuration(bug.elapsedSeconds, locale)}</span>}
      </div>

      <div className="row row--tight row--wrap">
        {bug.workSessionId && (
          <Link to={`/work/${bug.workSessionId}`} className="btn btn--sm">
            {t('live.viewSession')}
          </Link>
        )}
        {canReview && bug.state === 'ReadyForReview' && (
          <button type="button" className="btn btn--sm btn--primary" onClick={() => onAction('complete')}>
            {t('bugHunt.markComplete')}
          </button>
        )}
        {canReview && bug.state === 'Completed' && (
          <button type="button" className="btn btn--sm" onClick={() => onAction('reopen')}>
            {t('bugHunt.reopen')}
          </button>
        )}
        {canReview && bug.isAgentDiscovered && (
          <button type="button" className="btn btn--sm" onClick={() => onAction('confirm')}>
            {t('bugHunt.confirmBug')}
          </button>
        )}
        {/* QA: pick the fix up to test it, and send it back with a reason if it fails. */}
        {canReview && (bug.state === 'ReadyForReview' || bug.state === 'Testing' || bug.state === 'InProgress') && (
          <button type="button" className="btn btn--sm" onClick={() => onAction('test')}>
            <FlaskConical size={13} aria-hidden /> {t('bugHunt.startTesting')}
          </button>
        )}
        {canReview && bug.state === 'InTesting' && (
          <>
            <button type="button" className="btn btn--sm btn--primary" onClick={() => onAction('complete')}>
              {t('bugHunt.markComplete')}
            </button>
            <button type="button" className="btn btn--sm" onClick={() => setReturning((value) => !value)} aria-expanded={returning}>
              <Undo2 size={13} aria-hidden /> {t('bugHunt.sendBack')}
            </button>
          </>
        )}
      </div>

      {/* A bug goes back with the reason written down: the developer should not have to guess. */}
      {returning && (
        <form
          className="stack stack--tight"
          onSubmit={(event) => {
            event.preventDefault()
            const text = reason.trim()
            if (!text) return
            onAction('return', text)
            setReason('')
            setReturning(false)
          }}
        >
          <textarea
            className="input"
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('bugHunt.sendBackWhy')}
            maxLength={4000}
            aria-label={t('bugHunt.sendBackWhy')}
          />
          <div className="row row--tight">
            <button type="submit" className="btn btn--sm btn--primary" disabled={!reason.trim()}>{t('bugHunt.sendBack')}</button>
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => setReturning(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      )}

      <BugComments taskId={bug.taskId} canWrite={canReview} count={bug.commentCount} />
    </div>
  )
}

/* -------------------------------------------------------------------- list */

function BugRow({
  bug,
  developer,
  active,
  onClick,
}: {
  bug: BugEntity
  developer: DeveloperEntity | undefined
  active: boolean
  onClick: () => void
}) {
  const { t } = useI18n()
  const look = useMemo(() => (developer ? lookFor(developer.id, developer.avatar) : null), [developer])

  return (
    <button type="button" className={`bh-row${active ? ' bh-row--active' : ''}`} onClick={onClick} data-bug={bug.taskId}>
      <BugGlyph priority={bug.priority} size={30} />

      <span className="bh-row__body">
        <span className="row row--tight">
          <span className="mono small bold">{bug.externalId}</span>
          <Badge tone={priorityTone(bug.priority)}>{t(`priority.${bug.priority}`)}</Badge>
        </span>
        <span className="small truncate bh-row__title">{bug.title}</span>
        {bug.progressPercent > 0 && bug.state !== 'Completed' ? (
          <span className="bh-row__progress">
            <ProgressBar value={bug.progressPercent} size="sm" />
            <span className="tiny">{Math.round(bug.progressPercent)}%</span>
          </span>
        ) : (
          <span className="tiny muted">{t(`bugState.${bug.state}`)}</span>
        )}
      </span>

      <span className="bh-row__who">
        {developer && look ? (
          <>
            <Portrait look={look} size={24} />
            <span className="tiny truncate">{developer.name.split(' ')[0]}</span>
          </>
        ) : (
          <UserRound size={16} className="muted" aria-label={t('common.unassigned')} />
        )}
      </span>

      <ChevronRight size={14} className="muted" />
    </button>
  )
}

/** A mosquito small enough for a list row, from the same drawing as the floor. */
function BugGlyph({ priority, size }: { priority: Priority; size: number }) {
  return (
    <svg className="bh-glyph" width={size} height={size} viewBox="-27 -38 64 64" aria-hidden="true">
      <g className="bh-palette bh-palette--still">
        <BugCreature priority={priority} seed={3} weakened={false} still={false} />
      </g>
    </svg>
  )
}

/* ---------------------------------------------------------------- register */

/**
 * Registers a bug by hand — §53 of the Bug Hunt brief.
 *
 * The endpoint for this shipped with the first release and nothing called it, so the only way a
 * bug could enter containment was for an agent to mention it. A manager who knows about a bug
 * before any agent does had no way to put it on the board.
 *
 * It lands in Waiting, which is what the jar draws. It does not create a work session: starting
 * work is the agent's move, and inventing one here would put a session on the floor that no agent
 * is behind.
 */
function RegisterBugForm({ onClose }: { onClose: () => void }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()

  const [externalId, setExternalId] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('Medium')
  const [assignedDeveloperId, setAssignedDeveloperId] = useState('')
  const [specificationId, setSpecificationId] = useState('')

  const { data: developers } = useQuery({ queryKey: ['developers'], queryFn: api.settings.developers })
  const { data: specs } = useQuery({ queryKey: ['specs'], queryFn: () => api.specs.list() })

  const register = useMutation({
    mutationFn: () =>
      api.bugHunt.register({
        externalId,
        title,
        description: description || undefined,
        priority,
        assignedDeveloperId: assignedDeveloperId || undefined,
        specificationId: specificationId || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bug-hunt'] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      onClose()
    },
  })

  return (
    <form
      className="stack stack--tight bughunt__register"
      onSubmit={(event) => {
        event.preventDefault()
        if (externalId.trim() && title.trim()) register.mutate()
      }}
    >
      <div className="row row--tight">
        <input
          className="input mono"
          style={{ maxWidth: 130 }}
          value={externalId}
          onChange={(e) => setExternalId(e.target.value.toUpperCase())}
          placeholder="KNIT-2841"
          required
        />
        <select className="select" style={{ maxWidth: 120 }} value={priority} onChange={(e) => setPriority(e.target.value)}>
          {['Low', 'Medium', 'High', 'Critical'].map((value) => (
            <option key={value} value={value}>{t(`priority.${value}`)}</option>
          ))}
        </select>
      </div>

      <input
        className="input"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('tasks.taskTitle')}
        required
      />

      <textarea
        className="textarea"
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t('tasks.description')}
      />

      <select className="select" value={assignedDeveloperId} onChange={(e) => setAssignedDeveloperId(e.target.value)}>
        <option value="">{t('common.unassigned')}</option>
        {(developers ?? []).map((developer) => (
          <option key={developer.id} value={developer.id}>{developer.displayName}</option>
        ))}
      </select>

      {/* The PRD whose requirement this bug breaks. PRDs first: that is what a bug is raised against. */}
      <select className="select" value={specificationId} onChange={(e) => setSpecificationId(e.target.value)}>
        <option value="">{t('bugHunt.noSpec')}</option>
        {sortSpecs(specs?.specs ?? []).map((spec) => (
          <option key={spec.id} value={spec.id}>{spec.kind === 'Prd' ? 'PRD' : 'SDD'} · {spec.key} — {spec.title}</option>
        ))}
      </select>

      <p className="tiny muted">{t('bugHunt.registerHint')}</p>

      {register.error instanceof Error && (
        <p className="tiny" style={{ color: 'var(--danger-text)' }}>{register.error.message}</p>
      )}

      <div className="row row--tight">
        <button type="submit" className="btn btn--sm btn--primary" disabled={register.isPending}>
          {t('bugHunt.registerBug')}
        </button>
        <button type="button" className="btn btn--sm" onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </form>
  )
}

/** PRDs before SDDs, then by key: a bug is raised against a PRD far more often. */
function sortSpecs<T extends { kind: string; key: string }>(specs: T[]): T[] {
  return [...specs].sort((a, b) => (a.kind === b.kind ? a.key.localeCompare(b.key) : a.kind === 'Prd' ? -1 : 1))
}

/**
 * The PRD or SDD a bug was raised against, and the control to set it.
 *
 * Agents link most bugs themselves by naming the PRD when they start work. This is for the rest:
 * a bug somebody registered by hand, or one an agent picked up without saying.
 */
function SpecLink({ bug, canEdit }: { bug: BugEntity; canEdit: boolean }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const { data: specs } = useQuery({ queryKey: ['specs'], queryFn: () => api.specs.list(), enabled: editing })

  const link = useMutation({
    mutationFn: (specificationId: string) =>
      api.tasks.update(bug.taskId, { specificationId: specificationId || '00000000-0000-0000-0000-000000000000' }),
    onSuccess: () => {
      setEditing(false)
      void queryClient.invalidateQueries({ queryKey: ['bug-hunt'] })
      void queryClient.invalidateQueries({ queryKey: ['specs'] })
    },
  })

  if (editing) {
    return (
      <select
        className="select select--sm"
        autoFocus
        defaultValue={bug.specificationId ?? ''}
        onChange={(event) => link.mutate(event.target.value)}
        onBlur={() => setEditing(false)}
        disabled={link.isPending}
      >
        <option value="">{t('bugHunt.noSpec')}</option>
        {sortSpecs(specs?.specs ?? []).map((spec) => (
          <option key={spec.id} value={spec.id}>{spec.kind === 'Prd' ? 'PRD' : 'SDD'} · {spec.key} — {spec.title}</option>
        ))}
      </select>
    )
  }

  return (
    <div className="row row--tight tiny">
      <FileText size={12} className="muted" />
      {bug.specificationKey ? (
        <Link to={`/project/specs?spec=${bug.specificationId}`}>{bug.specificationKey}{bug.specificationTitle ? ` — ${bug.specificationTitle}` : ''}</Link>
      ) : (
        <span className="muted">{t('bugHunt.noSpec')}</span>
      )}
      {canEdit && (
        <button type="button" className="btn btn--sm btn--ghost" onClick={() => setEditing(true)}>
          {bug.specificationKey ? t('common.change') : t('bugHunt.linkSpec')}
        </button>
      )}
    </div>
  )
}

function priorityTone(priority: string): 'danger' | 'warn' | 'accent' | 'neutral' {
  if (priority === 'Critical') return 'danger'
  if (priority === 'High') return 'warn'
  if (priority === 'Medium') return 'accent'
  return 'neutral'
}

function stateTone(state: string): 'danger' | 'warn' | 'ok' | 'accent' | 'neutral' {
  if (state === 'Blocked') return 'danger'
  if (state === 'Testing' || state === 'ReadyForReview') return 'warn'
  if (state === 'Completed') return 'ok'
  if (state === 'InProgress') return 'accent'
  return 'neutral'
}
